import React, { useState, useEffect } from 'react';
import adminApi from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Star, Trash2, Check, X, Plus } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const ReviewsManager = () => {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', location: '', rating: 5, comment: '', productSlug: 'general' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/reviews');
      setReviews(data || []);
    } catch (e) {
      toast({ title: 'Erreur de chargement des avis', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const { data } = await adminApi.get('/api/public/products');
      setProducts(data || []);
    } catch (e) { /* non bloquant */ }
  };

  useEffect(() => { load(); loadProducts(); }, []);

  const addReview = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.comment.trim()) {
      toast({ title: 'Nom et avis obligatoires', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await adminApi.post('/api/admin/reviews', {
        name: form.name.trim(),
        location: form.location.trim(),
        rating: Number(form.rating),
        comment: form.comment.trim(),
        productSlug: form.productSlug || 'general',
      });
      toast({ title: 'Avis ajouté et publié' });
      setForm({ name: '', location: '', rating: 5, comment: '', productSlug: 'general' });
      load();
    } catch (e) {
      toast({ title: "Impossible d'ajouter l'avis", variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id) => {
    try { await adminApi.patch(`/api/admin/reviews/${id}`); load(); }
    catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const feature = async (id) => {
    try { await adminApi.patch(`/api/admin/reviews/${id}/feature`); load(); }
    catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try { await adminApi.delete(`/api/admin/reviews/${id}`); load(); }
    catch (e) { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const Stars = ({ n }) => (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= n ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
      ))}
    </span>
  );

  return (
    <div data-testid="admin-reviews-page">
      <h1 className="text-white text-2xl font-bold mb-2">Avis clients</h1>
      <p className="text-gray-400 text-sm mb-8">
        N'ajoutez que de <strong>vrais avis</strong> de clients. Ils apparaissent sur la page d'accueil uniquement
        lorsqu'ils sont publiés.
      </p>

      {/* Add form */}
      <form onSubmit={addReview} className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8 space-y-4" data-testid="admin-review-form">
        <h2 className="text-white font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Ajouter un avis</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input placeholder="Nom du client (ex. Sophie M.)" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" />
          <Input placeholder="Contexte (ex. Mariage à Lyon)" value={form.location}
                 onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="review-location-input" />
        </div>
        <div>
          <label className="text-gray-400 text-sm block mb-1">Associer à un kit (étoiles sur la fiche) :</label>
          <select
            value={form.productSlug}
            onChange={(e) => setForm({ ...form, productSlug: e.target.value })}
            className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white text-sm"
            data-testid="review-product-select"
          >
            <option value="general">Aucun — avis général (page d'accueil)</option>
            {products.map((p) => (
              <option key={p.slug} value={p.slug}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-sm">Note :</span>
          {[1, 2, 3, 4, 5].map((i) => (
            <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} data-testid={`review-star-${i}`}>
              <Star className={`w-6 h-6 ${i <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
            </button>
          ))}
        </div>
        <Textarea placeholder="Avis du client…" value={form.comment} rows={3}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="review-comment-input" />
        <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-500" data-testid="review-submit-btn">
          {saving ? 'Ajout…' : "Ajouter l'avis"}
        </Button>
      </form>

      {/* List */}
      {loading ? (
        <p className="text-gray-500">Chargement…</p>
      ) : reviews.length === 0 ? (
        <p className="text-gray-500">Aucun avis pour l'instant.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-white font-medium">{r.name}</span>
                  <Stars n={r.rating} />
                  {r.approved
                    ? <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">Publié</span>
                    : <span className="text-xs text-gray-400 bg-gray-700/40 px-2 py-0.5 rounded-full">Masqué</span>}
                  {r.featured && <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">★ Épinglé</span>}
                </div>
                {r.location ? <p className="text-purple-400 text-xs mb-1">{r.location}</p> : null}
                <p className="text-gray-300 text-sm">{r.comment}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => feature(r.id)} title="Épingler / désépingler"
                        className={r.featured ? 'text-amber-400' : 'text-gray-500'} data-testid={`review-feature-${r.id}`}>
                  <Star className={`w-4 h-4 ${r.featured ? 'fill-amber-400' : ''}`} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggle(r.id)} className="text-gray-300" data-testid={`review-toggle-${r.id}`}>
                  {r.approved ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-red-400" data-testid={`review-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsManager;
